// src/components/game/BinaryConverter.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScore } from '../../context/ScoreContext';

const BinaryConverter = ({ isDarkMode }) => {
  const { updateScore, getScore } = useScore();
  const [bestScore, setBestScore] = useState(getScore('binary'));
  const [binary, setBinary] = useState('');
  const [decimal, setDecimal] = useState('');
  const [error, setError] = useState('');
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [conversionCount, setConversionCount] = useState(0);
  const [isCorrect, setIsCorrect] = useState(false);
  const [time, setTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(() => {
    const saved = localStorage.getItem('binaryBestStreak');
    return saved ? parseInt(saved) : 0;
  });

  useEffect(() => {
    let timer;
    if (isPlaying) {
      timer = setInterval(() => setTime(t => t + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [isPlaying]);

  const validateBinary = (input) => {
    return /^[01]+$/.test(input);
  };

  const convertBinaryToDecimal = (binaryInput) => {
    if (!binaryInput) return '';
    if (!validateBinary(binaryInput)) {
      setError('⚠️ მხოლოდ 0 და 1!');
      return '';
    }
    setError('');
    const result = parseInt(binaryInput, 2);
    return result.toString();
  };

  const checkAnswer = (binaryVal, decimalVal) => {
    if (!binaryVal || !decimalVal) return false;
    if (!validateBinary(binaryVal)) return false;
    const correctDecimal = parseInt(binaryVal, 2);
    return parseInt(decimalVal) === correctDecimal;
  };

  const handleBinaryChange = (e) => {
    const value = e.target.value;
    setBinary(value);
    const result = convertBinaryToDecimal(value);
    setDecimal(result);
    setError('');
    setShowResult(false);
    setIsCorrect(false);

    if (value && result) {
      const correct = checkAnswer(value, result);
      if (correct) {
        setIsCorrect(true);
        setShowResult(true);
        setConversionCount(prev => prev + 1);
        setIsPlaying(true);
        
        const bonus = value.length > 6 ? 3 : value.length > 4 ? 2 : 1;
        const newScore = Math.min(20, score + bonus);
        if (newScore > score) {
          setScore(newScore);
          if (newScore > bestScore) {
            updateScore('binary', newScore);
            setBestScore(getScore('binary'));
          }
        }
        
        // Streak
        setStreak(prev => {
          const newStreak = prev + 1;
          if (newStreak > bestStreak) {
            setBestStreak(newStreak);
            localStorage.setItem('binaryBestStreak', newStreak.toString());
          }
          return newStreak;
        });
      } else {
        setStreak(0);
      }
    }
  };

  const handleDecimalChange = (e) => {
    const value = e.target.value;
    setDecimal(value);
    setError('');
    setShowResult(false);
    setIsCorrect(false);

    if (value) {
      const num = parseInt(value);
      if (!isNaN(num) && num >= 0 && num <= 255) {
        const binaryResult = num.toString(2);
        setBinary(binaryResult);
        setShowResult(true);
        setIsCorrect(true);
        setConversionCount(prev => prev + 1);
        setIsPlaying(true);
        
        const bonus = binaryResult.length > 6 ? 3 : binaryResult.length > 4 ? 2 : 1;
        const newScore = Math.min(20, score + bonus);
        if (newScore > score) {
          setScore(newScore);
          if (newScore > bestScore) {
            updateScore('binary', newScore);
            setBestScore(getScore('binary'));
          }
        }
        
        setStreak(prev => {
          const newStreak = prev + 1;
          if (newStreak > bestStreak) {
            setBestStreak(newStreak);
            localStorage.setItem('binaryBestStreak', newStreak.toString());
          }
          return newStreak;
        });
      } else if (num > 255) {
        setError('⚠️ მაქსიმუმ 255 (8-bit)');
        setBinary('');
        setStreak(0);
      } else {
        setError('⚠️ დადებითი რიცხვი!');
        setBinary('');
        setStreak(0);
      }
    } else {
      setBinary('');
      setError('');
      setStreak(0);
    }
  };

  const randomBinary = () => {
    const length = 3 + Math.floor(Math.random() * 6);
    let result = '';
    for (let i = 0; i < length; i++) {
      result += Math.random() > 0.5 ? '1' : '0';
    }
    setBinary(result);
    const dec = parseInt(result, 2);
    setDecimal(dec.toString());
    setError('');
    setShowResult(true);
    setIsCorrect(true);
    setConversionCount(prev => prev + 1);
    setIsPlaying(true);
    
    const bonus = result.length > 6 ? 3 : result.length > 4 ? 2 : 1;
    const newScore = Math.min(20, score + bonus);
    setScore(newScore);
    if (newScore > bestScore) {
      updateScore('binary', newScore);
      setBestScore(getScore('binary'));
    }
    
    setStreak(prev => {
      const newStreak = prev + 1;
      if (newStreak > bestStreak) {
        setBestStreak(newStreak);
        localStorage.setItem('binaryBestStreak', newStreak.toString());
      }
      return newStreak;
    });
  };

  const resetGame = () => {
    setBinary('');
    setDecimal('');
    setError('');
    setShowResult(false);
    setIsCorrect(false);
    setTime(0);
    setIsPlaying(false);
    setStreak(0);
  };

  const resetScore = () => {
    setScore(0);
    setConversionCount(0);
    setStreak(0);
    setTime(0);
    setIsPlaying(false);
    updateScore('binary', 0);
    setBestScore(getScore('binary'));
    localStorage.removeItem('binaryBestStreak');
    setBestStreak(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStreakEmoji = () => {
    if (streak >= 10) return '🔥';
    if (streak >= 5) return '⚡';
    if (streak >= 3) return '💪';
    return '📈';
  };

  const pageStyle = {
    minHeight: '100vh',
    background: isDarkMode 
      ? 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 100%)' 
      : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const containerStyle = {
    maxWidth: '550px',
    width: '100%',
    background: isDarkMode 
      ? 'rgba(26, 26, 46, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)',
    padding: 'clamp(20px, 4vw, 30px)',
    borderRadius: '20px',
    backdropFilter: 'blur(20px)',
    boxShadow: isDarkMode 
      ? '0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)' 
      : '0 20px 60px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)',
    border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
    animation: 'slideUp 0.6s ease-out'
  };

  const inputStyle = {
    width: '100%',
    padding: 'clamp(12px, 2vw, 16px)',
    fontSize: 'clamp(18px, 2.5vw, 22px)',
    fontFamily: 'monospace',
    borderRadius: '12px',
    border: '2px solid',
    outline: 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.02)',
    color: isDarkMode ? '#fff' : '#000'
  };

  return (
    <div style={pageStyle}>
      <style>
        {`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          .game-title {
            background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-size: 200% auto;
            animation: shimmer 3s linear infinite;
          }
          .streak-fire {
            animation: pulse 0.5s infinite;
          }
        `}
      </style>

      <div style={containerStyle}>
        <Link to="/games" style={{
          display: 'inline-block',
          padding: '8px 20px',
          marginBottom: '20px',
          background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
          color: isDarkMode ? '#fff' : '#000',
          textDecoration: 'none',
          borderRadius: '12px',
          transition: 'all 0.3s',
          backdropFilter: 'blur(10px)',
          border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateX(-5px)';
          e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateX(0)';
          e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
        }}>
          ← უკან
        </Link>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '15px',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <h1 style={{ margin: 0, fontSize: 'clamp(20px, 3vw, 28px)' }}>
            <span className="game-title">🔢 Binary Converter</span>
          </h1>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: '#4ade80', fontWeight: 'bold' }}>
              🏆 {bestScore}
            </span>
            <span style={{ color: isDarkMode ? '#aaa' : '#666' }}>
              📊 {score}
            </span>
          </div>
        </div>

        <p style={{ 
          color: isDarkMode ? '#aaa' : '#666',
          marginBottom: '20px',
          fontSize: 'clamp(12px, 1.5vw, 16px)'
        }}>
          გადაიყვანეთ ორობითი ↔ ათობითი (0-255)
        </p>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))',
          gap: '8px',
          marginBottom: '20px',
          padding: '15px',
          background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div>
            <div style={{ fontSize: '18px' }}>🔄</div>
            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{conversionCount}</div>
            <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>კონვერტაცია</div>
          </div>
          <div>
            <div style={{ fontSize: '18px' }}>⏱️</div>
            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{formatTime(time)}</div>
            <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>დრო</div>
          </div>
          <div>
            <div style={{ fontSize: '18px' }} className={streak >= 3 ? 'streak-fire' : ''}>
              {getStreakEmoji()}
            </div>
            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{streak}</div>
            <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>სტრიქი</div>
          </div>
          <div>
            <div style={{ fontSize: '18px' }}>🏆</div>
            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{bestStreak}</div>
            <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>საუკეთესო</div>
          </div>
        </div>

        {/* Binary Input */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ 
            color: isDarkMode ? '#fff' : '#000', 
            display: 'block', 
            marginBottom: '8px',
            fontWeight: 'bold',
            fontSize: 'clamp(12px, 1.5vw, 14px)'
          }}>
            📟 ორობითი (Binary):
          </label>
          <input
            type="text"
            value={binary}
            onChange={handleBinaryChange}
            placeholder="მაგ: 101010"
            style={{
              ...inputStyle,
              borderColor: error ? '#f87171' : 
                          (showResult && isCorrect ? '#4ade80' : 
                          (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')),
              boxShadow: showResult && isCorrect 
                ? '0 0 30px rgba(74,222,128,0.2)' 
                : error 
                  ? '0 0 30px rgba(248,113,113,0.2)' 
                  : 'none'
            }}
          />
        </div>

        {/* Decimal Input */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ 
            color: isDarkMode ? '#fff' : '#000', 
            display: 'block', 
            marginBottom: '8px',
            fontWeight: 'bold',
            fontSize: 'clamp(12px, 1.5vw, 14px)'
          }}>
            🔢 ათობითი (Decimal):
          </label>
          <input
            type="number"
            value={decimal}
            onChange={handleDecimalChange}
            placeholder="მაგ: 42"
            style={{
              ...inputStyle,
              borderColor: error ? '#f87171' : 
                          (showResult && isCorrect ? '#4ade80' : 
                          (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')),
              boxShadow: showResult && isCorrect 
                ? '0 0 30px rgba(74,222,128,0.2)' 
                : error 
                  ? '0 0 30px rgba(248,113,113,0.2)' 
                  : 'none'
            }}
            min="0"
            max="255"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px 16px',
            background: 'linear-gradient(135deg, #f87171, #ef4444)',
            borderRadius: '12px',
            color: '#fff',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            animation: 'slideUp 0.3s ease-out'
          }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* Success Message */}
        {showResult && !error && isCorrect && (
          <div style={{
            padding: '12px 16px',
            background: 'linear-gradient(135deg, #4ade80, #22d3ee)',
            borderRadius: '12px',
            color: '#000',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '10px',
            animation: 'slideUp 0.3s ease-out',
            fontWeight: 'bold'
          }}>
            <span style={{ fontSize: '20px' }}>✅</span>
            <span>სწორია! 🎯</span>
            <span style={{ 
              background: 'rgba(0,0,0,0.1)',
              padding: '2px 12px',
              borderRadius: '20px'
            }}>
              +{binary.length > 6 ? 3 : binary.length > 4 ? 2 : 1} ქულა
            </span>
          </div>
        )}

        {/* Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          <button onClick={randomBinary} style={{
            flex: 1,
            padding: '14px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: 'clamp(14px, 1.5vw, 16px)',
            cursor: 'pointer',
            transition: 'all 0.3s',
            minWidth: '80px',
            fontWeight: 'bold',
            boxShadow: '0 4px 20px rgba(102,126,234,0.3)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px) scale(1.02)';
            e.target.style.boxShadow = '0 8px 30px rgba(102,126,234,0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 4px 20px rgba(102,126,234,0.3)';
          }}>
            🎲 შემთხვევითი
          </button>
          <button onClick={resetGame} style={{
            flex: 1,
            padding: '14px',
            background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
            color: isDarkMode ? '#fff' : '#000',
            border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
            borderRadius: '12px',
            fontSize: 'clamp(14px, 1.5vw, 16px)',
            cursor: 'pointer',
            transition: 'all 0.3s',
            minWidth: '80px',
            backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
          }}>
            🔄 გასუფთავება
          </button>
          <button onClick={resetScore} style={{
            padding: '14px',
            background: 'linear-gradient(135deg, #f87171, #ef4444)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: 'clamp(14px, 1.5vw, 16px)',
            cursor: 'pointer',
            transition: 'all 0.3s',
            minWidth: '80px',
            fontWeight: 'bold',
            boxShadow: '0 4px 20px rgba(239,68,68,0.3)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px) scale(1.02)';
            e.target.style.boxShadow = '0 8px 30px rgba(239,68,68,0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 4px 20px rgba(239,68,68,0.3)';
          }}>
            🗑️ ქულები
          </button>
        </div>

        {/* Binary Table */}
        <div style={{
          marginTop: '20px',
          padding: '15px',
          background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.02)',
          borderRadius: '12px',
          fontSize: '12px',
          color: isDarkMode ? '#aaa' : '#666',
          border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)'
        }}>
          <details>
            <summary style={{ 
              cursor: 'pointer', 
              fontWeight: 'bold',
              padding: '5px 0',
              color: isDarkMode ? '#fff' : '#000'
            }}>
              📖 8-ბიტიანი ცხრილი
            </summary>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(8, 1fr)',
              gap: '5px',
              marginTop: '10px',
              textAlign: 'center'
            }}>
              {[128, 64, 32, 16, 8, 4, 2, 1].map((val, idx) => (
                <div key={idx} style={{
                  padding: '8px',
                  background: isDarkMode ? 'rgba(26,26,46,0.8)' : 'rgba(255,255,255,0.8)',
                  borderRadius: '8px',
                  border: '1px solid ' + (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'),
                  transition: 'all 0.3s'
                }}>
                  <div style={{ fontWeight: 'bold', fontSize: 'clamp(14px, 1.5vw, 16px)', color: isDarkMode ? '#fff' : '#000' }}>
                    {val}
                  </div>
                  <div style={{ fontSize: '10px', color: '#667eea' }}>2^{7-idx}</div>
                </div>
              ))}
            </div>
          </details>
        </div>

        {/* Keyboard shortcuts hint */}
        <div style={{
          marginTop: '15px',
          padding: '10px',
          textAlign: 'center',
          fontSize: '11px',
          color: isDarkMode ? '#555' : '#ccc',
          borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)'
        }}>
          💡 Enter-ზე დაჭერა ავტომატურად ამოწმებს
        </div>
      </div>
    </div>
  );
};

export default BinaryConverter;